/**
 * Popup zu einem Flurstück auf der Karte: Bezeichnung, Fläche und Status, darunter die
 * Eigentümer mit ihrem Status und die Notizen – das, was die Flächensicherung zum ausgewählten
 * Flurstück zeigt, nur zum Lesen. Der Titel führt in die Flächensicherung.
 *
 * Die Eigentümer lädt das Popup selbst, wenn es aufgeht; das Flurstück bringt sie nicht mit.
 */
import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import type { LatLngTuple } from "leaflet";
import {
  MapFeaturePopup,
  MapFeaturePopupSection,
  type ParcelData,
} from "../../../map";
import StatusChip from "../../../common/display/StatusChip";
import { ownersApi } from "../../../../services/api/owners";
import { useToast } from "../../../../contexts/ToastContext";
import { formatNumber } from "../../../../utils/formatters";
import { formatParcelLabel } from "../../../../utils/parcelLabel";
import { getParcelStatusVisual } from "../../../../utils/colors/parcelStatusUtils";
import { colors } from "../../../../styles/theme";

/** Notizen enden nach so vielen Zeilen, der Rest steht in der Flächensicherung */
const NOTES_MAX_LINES = 4;
/** Ab so vielen Eigentümern rollt die Liste im Popup */
const OWNERS_MAX_HEIGHT = 132;

interface ParcelOwner {
  id: string;
  name: string;
  status: string;
}

/**
 * Text der Notizen ohne Formatierung, Absätze und Listenpunkte als Zeilen. Die Notizen sind
 * HTML aus dem Editor; `DOMParser` führt darin nichts aus, `textContent` liest nur den Text.
 */
function notesToPlainText(html: string): string {
  const withBreaks = html.replace(/<\/(p|li)>|<br\s*\/?>/gi, "$&\n");
  const text =
    new DOMParser().parseFromString(withBreaks, "text/html").body.textContent ??
    "";
  return text.replace(/\n{2,}/g, "\n").trim();
}

interface ParcelPopupProps {
  projectId: string;
  parcel: ParcelData;
  position: LatLngTuple;
  onClose: () => void;
}

const ParcelPopup: React.FC<ParcelPopupProps> = ({
  projectId,
  parcel,
  position,
  onClose,
}) => {
  const navigate = useNavigate();
  const { showError } = useToast();
  /** `null`, solange sie laden */
  const [owners, setOwners] = useState<ParcelOwner[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    ownersApi
      .getParcelOwners(projectId, parcel.id)
      .then((data) => {
        if (!cancelled) setOwners(data);
      })
      .catch(() => {
        if (cancelled) return;
        setOwners([]);
        showError("Eigentümer konnten nicht geladen werden");
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, parcel.id, showError]);

  const area = parcel.projektFlaeche ?? parcel.flaeche;
  const notes = parcel.notizen ? notesToPlainText(parcel.notizen) : "";

  return (
    <MapFeaturePopup
      position={position}
      onClose={onClose}
      kind="Flurstück"
      title={formatParcelLabel(parcel)}
      titleLink={{
        tooltip: "In der Flächensicherung öffnen",
        onClick: () => navigate(`/project/${projectId}/area`),
      }}
      subtitle={
        <>
          {area != null && <span>{formatNumber(area, 2)} m²</span>}
          <StatusChip
            {...getParcelStatusVisual(parcel.status)}
            variante="subtle"
          />
        </>
      }
    >
      <MapFeaturePopupSection label="Eigentümer">
        {owners === null ? (
          <CircularProgress size={16} />
        ) : owners.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.text.secondary }}>
            Keine Eigentümer erfasst
          </Typography>
        ) : (
          <Box sx={{ maxHeight: OWNERS_MAX_HEIGHT, overflowY: "auto" }}>
            {owners.map((owner) => (
              <Box
                key={owner.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  py: 0.25,
                }}
              >
                <Typography variant="body2" sx={{ minWidth: 0 }}>
                  {owner.name || "-"}
                </Typography>
                <StatusChip
                  {...getParcelStatusVisual(owner.status)}
                  variante="subtle"
                />
              </Box>
            ))}
          </Box>
        )}
      </MapFeaturePopupSection>

      {notes && (
        <MapFeaturePopupSection label="Notizen">
          <Typography
            variant="body2"
            sx={{
              backgroundColor: colors.background.default,
              borderRadius: 1,
              px: 1.25,
              py: 0.75,
              whiteSpace: "pre-line",
              display: "-webkit-box",
              WebkitLineClamp: NOTES_MAX_LINES,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {notes}
          </Typography>
        </MapFeaturePopupSection>
      )}
    </MapFeaturePopup>
  );
};

export default ParcelPopup;
